import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CertificationDeficiency {
  code: string;
  detail?: string;
  /** Advisory deficiencies (media) carry blocking=false and never fail a lesson. */
  blocking?: boolean;
}

export interface CertificationRow {
  lesson_id: string;
  status: "CERTIFIED" | "REQUIRES_FIX" | "BLOCKED" | "EXCLUDED_FIXTURE";
  policy_version: string;
  competency_key: string | null;
  competency_version: number | null;
  has_video: boolean;
  has_infographic: boolean;
  media_complete: boolean;
  invalidated_at: string | null;
  evaluated_at: string;
  deficiencies: CertificationDeficiency[];
  lessons?: { lesson_name: string | null; course_name: string | null } | null;
}

export interface CertificationSummary {
  total_lessons: number;
  total_courses: number;
  evaluated: number;
  by_status: Record<string, number>;
  exactly_one_validated_primary: number;
  missing_video: number;
  missing_infographic: number;
  stale: number;
  by_deficiency: Record<string, number>;
  by_course: { course: string; total: number; certified: number; requires_fix: number; blocked: number }[];
}

/** Admin-only aggregate certification counts for the whole curriculum. */
export function useCurriculumCertificationSummary() {
  return useQuery({
    queryKey: ["curriculum-certification", "summary"],
    queryFn: async (): Promise<CertificationSummary> => {
      const { data, error } = await supabase.rpc("get_curriculum_certification_summary");
      if (error) throw error;
      return data as unknown as CertificationSummary;
    },
    staleTime: 30_000,
  });
}

export interface CertificationFilters {
  status?: string;
  course?: string;
  competencyKey?: string;
  deficiencyCode?: string;
  media?: "all" | "complete" | "missing_video" | "missing_infographic";
  search?: string;
}

/** Admin-only per-lesson certification rows, filtered server-side. */
export function useCurriculumCertificationRows(filters: CertificationFilters) {
  return useQuery({
    queryKey: ["curriculum-certification", "rows", filters],
    queryFn: async (): Promise<CertificationRow[]> => {
      let query = supabase
        .from("lesson_certification_status")
        .select("lesson_id,status,policy_version,competency_key,competency_version,has_video,has_infographic,media_complete,invalidated_at,evaluated_at,deficiencies,lessons(lesson_name,course_name)")
        .order("status", { ascending: true })
        .limit(1000);

      if (filters.status && filters.status !== "all") query = query.eq("status", filters.status);
      if (filters.competencyKey) query = query.eq("competency_key", filters.competencyKey);
      if (filters.media === "complete") query = query.eq("media_complete", true);
      if (filters.media === "missing_video") query = query.eq("has_video", false);
      if (filters.media === "missing_infographic") query = query.eq("has_infographic", false);

      const { data, error } = await query;
      if (error) throw error;

      let rows = (data ?? []) as unknown as CertificationRow[];
      if (filters.course && filters.course !== "all") {
        rows = rows.filter((r) => r.lessons?.course_name === filters.course);
      }
      if (filters.deficiencyCode && filters.deficiencyCode !== "all") {
        rows = rows.filter((r) => (r.deficiencies ?? []).some((d) => d.code === filters.deficiencyCode));
      }
      if (filters.search) {
        const q = filters.search.toLowerCase();
        rows = rows.filter((r) => (r.lessons?.lesson_name ?? "").toLowerCase().includes(q));
      }
      return rows;
    },
    staleTime: 15_000,
  });
}

/** Re-run certification for one lesson (admin only, server-validated). */
export function useRecertifyLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lessonId: string) => {
      const { data, error } = await supabase.rpc("admin_recertify_lesson", { p_lesson_id: lessonId });
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["curriculum-certification"] }),
  });
}

/** Resumable, idempotent batch certification (admin only, server-validated). */
export function useCertifyBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ limit, mode }: { limit: number; mode: "pending" | "failing" | "all" }) => {
      const { data, error } = await supabase.rpc("certify_curriculum_batch", { p_limit: limit, p_mode: mode });
      if (error) throw error;
      return data as unknown as { processed: number; remaining: number; totals: Record<string, number> };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["curriculum-certification"] }),
  });
}
